import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react'
import PropTypes from 'prop-types'

import { TwoFaInputWrapper, StyledNumberField } from './styles'

export type AuthCodeProps = {
  autoFocus?: boolean
  disabled?: boolean
  length?: number
  onChange: (res: string) => void
}

export type AuthCodeRef = {
  focus: () => void
  clear: () => void
}

const numeric = {
  type: 'text',
  inputMode: 'numeric',
  pattern: '[0-9]{1}',
  min: '0',
  max: '9',
}

const AuthCode = forwardRef<AuthCodeRef, AuthCodeProps>(
  ({ autoFocus = true, disabled, length = 6, onChange }, ref) => {
    const inputsRef = useRef<Array<HTMLInputElement>>([])

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputsRef.current) {
          inputsRef.current[0].focus()
        }
      },
      clear: () => {
        if (inputsRef.current) {
          for (let i = 0; i < inputsRef.current.length; i++) {
            inputsRef.current[i].value = ''
          }
          inputsRef.current[0].focus()
        }
        sendResult()
      },
    }))

    useEffect(() => {
      if (autoFocus) {
        inputsRef.current[0].focus()
      }
    }, [autoFocus])

    const sendResult = () => {
      const res = inputsRef.current.map((input) => input.value).join('')
      onChange && onChange(res)
    }

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { value, nextElementSibling },
      } = e
      if (value.length > 1) {
        e.target.value = value.charAt(0)
        if (nextElementSibling !== null) {
          ;(nextElementSibling as HTMLInputElement).focus()
        }
      } else {
        if (value.match(numeric.pattern)) {
          if (nextElementSibling !== null) {
            ;(nextElementSibling as HTMLInputElement).focus()
          }
        } else {
          e.target.value = ''
        }
      }
      sendResult()
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key } = e
      const target = e.target as HTMLInputElement
      if (key === 'Backspace') {
        if (target.value === '') {
          if (target.previousElementSibling !== null) {
            const t = target.previousElementSibling as HTMLInputElement
            t.value = ''
            t.focus()
            e.preventDefault()
          }
        } else {
          target.value = ''
        }
        sendResult()
      }
    }

    const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select()
    }

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = e.clipboardData.getData('Text')

      let currentInput = 0

      for (let i = 0; i < pastedValue.length; i++) {
        const pastedCharacter = pastedValue.charAt(i)
        const currentValue = inputsRef.current[currentInput].value
        if (pastedCharacter.match(numeric.pattern)) {
          if (!currentValue) {
            inputsRef.current[currentInput].value = pastedCharacter
            if (inputsRef.current[currentInput].nextElementSibling !== null) {
              ;(
                inputsRef.current[currentInput]
                  .nextElementSibling as HTMLInputElement
              ).focus()
              currentInput++
            }
          }
        }
      }
      sendResult()

      e.preventDefault()
    }

    const inputs = []
    for (let i = 0; i < length; i++) {
      inputs.push(
        <StyledNumberField
          key={i}
          onChange={handleOnChange}
          onKeyDown={handleOnKeyDown}
          onFocus={handleOnFocus}
          onPaste={handleOnPaste}
          type={numeric.type}
          ref={(el: HTMLInputElement) => {
            inputsRef.current[i] = el
          }}
          maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Character ${i + 1}.`}
          disabled={disabled}
        />
      )
    }

    return <TwoFaInputWrapper>{inputs}</TwoFaInputWrapper>
  }
)

AuthCode.displayName = 'AuthCode'
AuthCode.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  length: PropTypes.number,
  onChange: PropTypes.any,
}

export default AuthCode
